import { FC, useMemo } from 'react'

import { AccountCircle } from '@mui/icons-material'
import { Grid, Avatar, Typography, GridProps, AvatarGroupProps, AvatarGroup } from '@mui/material'

import { Analyst as TAnalyst } from '../../types/Analyst'
import './Analyst.scss'

/**
 * Component for displaying analyst image, name and title
 */
type AnalystProps = GridProps & {
  name?: string
  picture?: string
  title?: string
  icon?: React.ReactElement
  size?: 'small' | 'large'
}

export const Analyst: FC<AnalystProps> = ({ name = 'GameRefinery', picture, title = 'Analyst', icon, size = 'large', ...gridProps }) => {
  return (
    <Grid container alignItems="center" spacing={2} {...gridProps} className={`Analyst${size === 'small' ? ' small' : ''}`}>
      <Grid item>
        {picture ? (
          <Avatar alt={name} src={picture} />
        ) : icon ? (
          <Avatar className="icon" alt={name}>
            {icon}
          </Avatar>
        ) : (
          <AccountCircle color="secondary" />
        )}
      </Grid>
      <Grid item>
        <Typography variant="h3" textAlign={gridProps.direction === 'row-reverse' ? 'right' : undefined}>
          {name}
        </Typography>
        <Typography variant="h5" sx={{ mb: 0.5 }} textAlign={gridProps.direction === 'row-reverse' ? 'right' : undefined} className="Analyst__Title">
          {title}
        </Typography>
      </Grid>
    </Grid>
  )
}

/**
 * Component for displaying multiple analysts as avatar group
 */
type AnalystsProps = AvatarGroupProps & {
  analysts: TAnalyst[]
  shuffle?: boolean
}

export const Analysts: FC<AnalystsProps> = ({ analysts, shuffle = true, ...avatarGroupProps }) => {
  // Randomize the order of analysts just to be fair for the analysts
  const shuffledAnalysts = useMemo(() => {
    return shuffle ? [...analysts].sort(() => Math.random() - 0.5) : analysts
  }, [analysts, shuffle])

  return (
    <AvatarGroup max={4} {...avatarGroupProps}>
      {shuffledAnalysts.map((analyst) => (
        <Avatar key={analyst.id} alt={analyst.name} src={analyst.picture} title={`${analyst.name}${analyst.title ? ` - ${analyst.title}` : ''}`} />
      ))}
    </AvatarGroup>
  )
}
